import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { ChangePasswordDto } from '@api-proxies';
import { UserService } from '@http/clients/user.service';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { IconSanitizerService, ModalComponentBase, SnackbarNotificationService } from 'ngx-lib';
import {
    passwordsMatchValidator,
    requiredTrimValidator
} from '../../password-input/password-input-validators/password-input-validators';
import { PasswordInputComponent } from '../../password-input/password-input.component';

@Component({
    selector: 'app-modal-update-password',
    templateUrl: './modal-update-password.component.html',
    styleUrls: ['./modal-update-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ReactiveFormsModule, ...CDK_IMPORTS, PasswordInputComponent]
})
export class ModalUpdatePasswordComponent extends ModalComponentBase<undefined, undefined> {
    updatePasswordForm: FormGroup;

    closeIcon: SafeHtml;

    constructor(
        private readonly fb: FormBuilder,
        private readonly awaiter: AwaiterService,
        private readonly userService: UserService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.updatePasswordForm = this.fb.group(
            {
                currentPassword: ['', [requiredTrimValidator()]],
                newPassword: ['', [requiredTrimValidator(), Validators.minLength(6), Validators.maxLength(100)]],
                confirmPassword: ['', [requiredTrimValidator()]]
            },
            {
                validators: passwordsMatchValidator('newPassword', 'confirmPassword')
            }
        );
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
    }

    get currentPasswordControl(): FormControl {
        return this.updatePasswordForm.get('currentPassword') as FormControl;
    }

    get newPasswordControl(): FormControl {
        return this.updatePasswordForm.get('newPassword') as FormControl;
    }

    get confirmPasswordControl(): FormControl {
        return this.updatePasswordForm.get('confirmPassword') as FormControl;
    }

    async submitUpdateForm(): Promise<void> {
        if (!this.updatePasswordForm.valid) return;

        const bodyRequestDto: ChangePasswordDto = {
            currentPassword: this.updatePasswordForm.value['currentPassword'],
            newPassword: this.updatePasswordForm.value['newPassword'],
            confirmNewPassword: this.updatePasswordForm.value['confirmPassword']
        };

        await this.awaiter.awaitAction('Saving new password ...', async () => {
            await this.userService.sendChangePasswordRequest(bodyRequestDto);
            this.snackbarNotificationService.success('Password successfully updated');
            this.close(undefined);
        });
    }
}
